import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState, useCallback, useEffect } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import { contactsService } from "@/api/services/contacts.service";
import { quotesService } from "@/api/services/quotes.service";
import { useJob } from "../hooks/useJob";
import { useDilapidation } from "./hooks/useDilapidation";
import { useDilapidationFeedback } from "./hooks/useDilapidationFeedback";
import { useAlert } from "@/hooks/useAlert";
import { formatJobAddress } from "@/utils/address";
import { formatFullName } from "@/utils/string";
import { formatDateOnlyDisplay } from "@/utils/dateTime";
import { dilapidationStatusDisplayLabel } from "./dilapidationStatusLabels";
import styles from "./DilapidationReportView.module.css";
const formatDate = (dateString) => !dateString ? "[DD-MMM-YYYY]" : formatDateOnlyDisplay(dateString);
const IMAGE_EXTENSIONS = ["jpg", "jpeg", "png", "gif", "webp", "bmp"];
const isImageDocument = (doc) => {
    // SVG must always be treated as file (not inline image)
    const ext = (doc.fileExtension ??
        doc.fileName?.split(".").pop() ??
        "").toLowerCase();
    if (ext === "svg")
        return false;
    if (doc.fileName?.toLowerCase().endsWith(".svg"))
        return false;
    if (doc.fileExtension?.toLowerCase() === "svg")
        return false;
    if (doc.type?.toLowerCase() === "image")
        return true;
    return IMAGE_EXTENSIONS.includes(ext);
};
const BackIcon = () => (_jsx("svg", { width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: _jsx("path", { d: "M19 12H5M12 19l-7-7 7-7" }) }));
const getStatusBadgeClass = (status) => {
    switch (status) {
        case "Draft":
            return styles.statusBadgeDraft;
        case "Public":
            return styles.statusBadgePublic;
        case "Approved":
            return styles.statusBadgeApproved;
        default:
            return styles.statusBadgeDraft;
    }
};
export const DilapidationReportView = () => {
    const { jobId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { toast } = useAlert();
    const isStaff = location.pathname.startsWith("/admin") ||
        location.pathname.startsWith("/staff");
    const isClient = location.pathname.startsWith("/client");
    const basePath = location.pathname.startsWith("/staff") ? "/staff" : "/admin";
    const { job } = useJob(jobId);
    const { dilapidation, loading, error, notFound, refetch } = useDilapidation(jobId);
    const { submitFeedback, loading: feedbackLoading } = useDilapidationFeedback(jobId, useCallback(() => {
        refetch();
    }, [refetch]));
    const [agreeChecked, setAgreeChecked] = useState(false);
    const [comments, setComments] = useState("");
    const [contactDisplayName, setContactDisplayName] = useState(null);
    const [attentionDisplayName, setAttentionDisplayName] = useState(null);
    useEffect(() => {
        if (!job?.contactId) {
            setContactDisplayName(null);
            return;
        }
        let cancelled = false;
        contactsService
            .getContact(job.contactId)
            .then((response) => {
            if (cancelled)
                return;
            const contact = response.data;
            const company = (contact.companyName ?? "").trim() ||
                (job?.clientName ?? "").trim();
            setContactDisplayName(company ? company : null);
        })
            .catch(() => {
            if (!cancelled)
                setContactDisplayName(null);
        });
        return () => {
            cancelled = true;
        };
    }, [job?.contactId, job?.clientName]);
    useEffect(() => {
        if (!job?.quoteId) {
            setAttentionDisplayName(null);
            return;
        }
        let cancelled = false;
        quotesService
            .getQuote(job.quoteId)
            .then((res) => {
            if (cancelled)
                return;
            const q = res.data;
            const trimmed = q.attentionContactName?.trim();
            if (trimmed) {
                setAttentionDisplayName(trimmed);
                return;
            }
            if (q.attentionContactId) {
                contactsService
                    .getContact(q.attentionContactId)
                    .then((cr) => {
                    if (cancelled)
                        return;
                    const name = formatFullName(cr.data.firstName, cr.data.lastName);
                    setAttentionDisplayName(name.trim() ? name : null);
                })
                    .catch(() => {
                    if (!cancelled)
                        setAttentionDisplayName(null);
                });
            }
            else {
                setAttentionDisplayName(null);
            }
        })
            .catch(() => {
            if (!cancelled)
                setAttentionDisplayName(null);
        });
        return () => {
            cancelled = true;
        };
    }, [job?.quoteId]);
    const handleBack = useCallback(() => {
        if (isClient) {
            navigate(`/client/jobs/${jobId}`);
        }
        else {
            navigate(`${basePath}/jobs/${jobId}/edit`);
        }
    }, [isClient, basePath, jobId, navigate]);
    const handleSubmitFeedback = useCallback(async () => {
        if (!jobId)
            return;
        const result = await submitFeedback(false, comments, false);
        if (result) {
            toast.success("Feedback submitted successfully.");
            setComments(result.feedback ?? "");
        }
        else {
            toast.error("Failed to submit feedback.");
        }
    }, [jobId, submitFeedback, comments, toast]);
    const handleApprove = useCallback(async () => {
        if (!jobId || !agreeChecked)
            return;
        const result = await submitFeedback(true, comments, true);
        if (result) {
            toast.success("Report approved successfully.");
            setAgreeChecked(false);
            setComments("");
            refetch();
        }
        else {
            toast.error("Failed to approve report.");
        }
    }, [jobId, agreeChecked, comments, submitFeedback, toast, refetch]);
    const status = dilapidation?.status ?? "Draft";
    const isApproved = status === "Approved";
    const canApprove = isClient && status === "Public" && !isApproved;
    useEffect(() => {
        if (isClient && canApprove && dilapidation?.feedback && comments === "") {
            setComments(dilapidation.feedback);
        }
    }, [dilapidation?.feedback, isClient, canApprove, comments]);
    const visibleSections = (dilapidation?.sections ?? [])
        .filter((s) => s.checkBox !== false)
        .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
    if (loading && !dilapidation) {
        return (_jsx("div", { className: styles.page, children: _jsxs("header", { className: styles.header, children: [_jsx("button", { type: "button", className: styles.backButton, onClick: handleBack, "aria-label": "Back", children: _jsx(BackIcon, {}) }), _jsxs("div", { className: styles.headerContent, children: [_jsx("h1", { className: styles.headerTitle, children: "Dilapidation Report" }), _jsx("p", { className: styles.loadingText, children: "Loading..." })] })] }) }));
    }
    if (error) {
        return (_jsx("div", { className: styles.page, children: _jsxs("header", { className: styles.header, children: [_jsx("button", { type: "button", className: styles.backButton, onClick: handleBack, "aria-label": "Back", children: _jsx(BackIcon, {}) }), _jsxs("div", { className: styles.headerContent, children: [_jsx("h1", { className: styles.headerTitle, children: "Dilapidation Report" }), _jsx("p", { className: styles.errorText, children: error })] })] }) }));
    }
    if (notFound && !dilapidation) {
        return (_jsxs("div", { className: styles.page, children: [_jsxs("header", { className: styles.header, children: [_jsx("button", { type: "button", className: styles.backButton, onClick: handleBack, "aria-label": "Back", children: _jsx(BackIcon, {}) }), _jsxs("div", { className: styles.headerContent, children: [_jsx("h1", { className: styles.headerTitle, children: "Dilapidation Report" }), job && (_jsxs("p", { className: styles.headerSubtitle, children: ["Job #", job.jobNumber, " \u2022 ", formatJobAddress(job)] }))] })] }), _jsx("div", { className: styles.reportContainer, children: _jsxs("div", { className: styles.emptyState, children: [_jsx("h3", { className: styles.emptyStateTitle, children: isStaff
                                    ? "No dilapidation report yet"
                                    : "No dilapidation report available" }), _jsx("p", { className: styles.emptyStateText, children: isStaff
                                    ? "Create a dilapidation report for this job to get started."
                                    : "The dilapidation report has not been created yet." }), isStaff && jobId && (_jsx(Link, { to: `${basePath}/jobs/${jobId}/dilapidation/edit`, className: styles.createLink, children: "Create Dilapidation Report" }))] }) })] }));
    }
    if (isClient && status === "Draft") {
        return (_jsxs("div", { className: styles.page, children: [_jsxs("header", { className: styles.header, children: [_jsx("button", { type: "button", className: styles.backButton, onClick: handleBack, "aria-label": "Back", children: _jsx(BackIcon, {}) }), _jsxs("div", { className: styles.headerContent, children: [_jsx("h1", { className: styles.headerTitle, children: "Dilapidation Report" }), job && (_jsxs("p", { className: styles.headerSubtitle, children: ["Job #", job.jobNumber, " \u2022 ", formatJobAddress(job)] }))] })] }), _jsx("div", { className: styles.reportContainer, children: _jsx("div", { className: styles.draftMessage, children: _jsx("p", { className: styles.draftMessageText, children: "Report not yet published." }) }) })] }));
    }
    const statusBadgeClass = `${styles.statusBadgeReport} ${getStatusBadgeClass(status)}`;
    return (_jsxs("div", { className: styles.page, children: [_jsxs("header", { className: styles.header, children: [_jsx("button", { type: "button", className: styles.backButton, onClick: handleBack, "aria-label": "Back", children: _jsx(BackIcon, {}) }), _jsx("h1", { className: styles.headerTitle, children: "Dilapidation Report" })] }), _jsx("div", { className: styles.reportContainer, children: _jsxs("div", { className: styles.darkBackground, children: [_jsx("div", { className: styles.logoContainer, children: _jsx("img", { src: "/logo-white.png", alt: "The Defit Group", className: styles.logo }) }), _jsxs("div", { className: styles.topRightActions, children: [!isClient && (_jsx("span", { className: statusBadgeClass, children: dilapidationStatusDisplayLabel(status) })), _jsx("div", { className: styles.dateBadge, children: formatDate(dilapidation?.createdAt) }), isStaff && jobId && (_jsx(Link, { to: `${basePath}/jobs/${jobId}/dilapidation/edit`, className: styles.editLink, children: "Edit Report" }))] }), _jsx("div", { className: styles.documentTitle, children: "Dilapidation Report" }), _jsxs("div", { className: styles.contentCard, children: [_jsxs("div", { className: styles.infoSection, children: [_jsxs("p", { className: styles.infoLine, children: [_jsx("strong", { children: "Client:" }), " ", contactDisplayName || "[Company Name]"] }), _jsxs("p", { className: styles.infoLine, children: [_jsx("strong", { children: "Attention:" }), " ", attentionDisplayName?.trim() || "[Contact Name]"] }), _jsxs("p", { className: styles.infoLine, children: [_jsx("strong", { children: "Job No.:" }), " ", job?.jobNumber ?? "[Job No.]"] }), _jsxs("p", { className: styles.infoLine, children: [_jsx("strong", { children: "Date:" }), " ", formatDate(dilapidation?.createdAt)] })] }), visibleSections.map((section, index) => (_jsxs(React.Fragment, { children: [index > 0 && _jsx("hr", { className: styles.sectionDivider }), _jsxs("section", { className: styles.section, children: [section.sectionName && (_jsx("h2", { className: styles.sectionTitle, children: section.sectionName })), section.subHeading && (_jsx("h3", { className: styles.sectionSubHeading, children: section.subHeading })), section.content && (_jsx("div", { className: styles.sectionContent, dangerouslySetInnerHTML: { __html: section.content } })), section.documents && section.documents.length > 0 && (_jsxs(_Fragment, { children: [section.documents
                                                            .filter(isImageDocument)
                                                            .some((d) => d.url) && (_jsx("div", { className: styles.imagesRow, children: section.documents
                                                                .filter(isImageDocument)
                                                                .filter((d) => !!d.url)
                                                                .map((doc) => (_jsx("a", { href: doc.url, target: "_blank", rel: "noopener noreferrer", className: styles.sectionImageWrapper, children: _jsx("img", { src: doc.url, alt: doc.fileName ?? "Image", className: styles.sectionImage }) }, doc.id))) })), section.documents.filter((d) => !isImageDocument(d))
                                                            .length > 0 && (_jsx("ul", { className: styles.documentsList, children: section.documents
                                                                .filter((d) => !isImageDocument(d))
                                                                .map((doc) => (_jsx("li", { children: _jsx("a", { href: doc.url ?? "#", target: "_blank", rel: "noopener noreferrer", className: styles.documentLink, children: doc.fileName ?? "Document" }) }, doc.id))) }))] }))] })] }, section.id))), isStaff && dilapidation?.feedback && (_jsxs("div", { className: styles.feedbackSection, children: [_jsx("h3", { className: styles.feedbackSectionTitle, children: "Client Feedback" }), dilapidation.isApprove && (_jsx("p", { className: styles.approvedState, children: "Client has approved this report." })), _jsx("p", { className: styles.feedbackText, children: dilapidation.feedback })] })), canApprove && (_jsxs("div", { className: styles.feedbackSection, children: [_jsx("h3", { className: styles.feedbackSectionTitle, children: "Approve or Comment" }), _jsxs("div", { className: styles.approveForm, children: [_jsxs("div", { className: styles.approveCheckbox, children: [_jsx("input", { type: "checkbox", id: "agree", checked: agreeChecked, onChange: (e) => setAgreeChecked(e.target.checked) }), _jsx("label", { htmlFor: "agree", children: "I have reviewed this report and agree with the findings." })] }), _jsx("label", { htmlFor: "comments", children: "Additional Comments or Suggestions (optional)" }), _jsx("textarea", { id: "comments", className: styles.commentsTextarea, value: comments, onChange: (e) => setComments(e.target.value), placeholder: "Enter any additional comments..." }), _jsxs("div", { className: styles.approveButtons, children: [_jsx("button", { type: "button", className: styles.submitBtn, onClick: handleSubmitFeedback, disabled: feedbackLoading, children: "Submit Feedback" }), _jsx("button", { type: "button", className: styles.approveBtn, onClick: handleApprove, disabled: !agreeChecked || feedbackLoading, children: "Approve" })] })] })] })), isClient && isApproved && (_jsxs("div", { className: styles.feedbackSection, children: [_jsx("p", { className: styles.approvedState, children: "You have approved this report." }), dilapidation?.feedback && (_jsx("p", { className: styles.feedbackText, children: dilapidation.feedback }))] }))] })] }) })] }));
};
